import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { moduleService } from '@/services/moduleService'
import { instructorSplitService } from '@/services/instructorSplitService'
import type { Module } from '@/types/module'

export function useModulesByCourse(courseId: string) {
  return useQuery({
    queryKey: ['modules', 'course', courseId],
    queryFn: async () => {
      const response = await moduleService.getByCourseId(courseId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch modules')
      }
      return response.data as Module[]
    },
    enabled: !!courseId,
  })
}

export function useModule(id: string) {
  return useQuery({
    queryKey: ['modules', id],
    queryFn: async () => {
      const response = await moduleService.getById(id)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch module')
      }
      return response.data
    },
    enabled: !!id,
  })
}

// Hook untuk instructor - fetch only assigned modules from assigned courses
export function useInstructorModules(userId?: string) {
  return useQuery({
    queryKey: ['modules', 'instructor', userId],
    queryFn: async () => {
      // 1. Fetch instructor's assigned courses only
      const coursesResponse = await instructorSplitService.getCoursesByInstructor(userId!)
      if (!coursesResponse.success || !coursesResponse.data) {
        throw new Error('Failed to fetch instructor courses')
      }

      const courses = coursesResponse.data

      // 2. For each assigned course, fetch assigned modules
      const modulesPromises = courses.map(async (course) => {
        const modulesResponse = await instructorSplitService.getAssignedModules(userId!, course.courseId)
        if (!modulesResponse.success || !modulesResponse.data) {
          return []
        }

        // Add course info to each module for display
        return modulesResponse.data.map((module: Module) => ({
          ...module,
          courseName: course.courseTitle,
        }))
      })

      const modulesArrays = await Promise.all(modulesPromises)
      // Flatten array of arrays into single array
      return modulesArrays.flat()
    },
    enabled: !!userId,
  })
}

export function useCreateModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Parameters<typeof moduleService.create>[0]) =>
      moduleService.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['modules', 'course', variables.courseId] })
    },
  })
}

export function useUpdateModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof moduleService.update>[1] }) =>
      moduleService.update(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['modules', response.data.id] })
        queryClient.invalidateQueries({ queryKey: ['modules', 'course', response.data.courseId] })
      }
    },
  })
}

export function useDeleteModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => moduleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
    },
  })
}

export function usePublishModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => moduleService.publish(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['modules', response.data.id] })
        queryClient.invalidateQueries({ queryKey: ['modules', 'course', response.data.courseId] })
      }
    },
  })
}

export function useReorderModules() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ courseId, moduleIds }: { courseId: string; moduleIds: string[] }) =>
      moduleService.reorder(courseId, moduleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
    },
  })
}
